/**
 * Reversion Triggers — bank-style, 14 vignettes.
 */

import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import {
  type ReversionAction,
  type ReversionVignette,
  ACTION_LABEL,
  REVERSION_TRIGGER_BANK,
} from "@/features/quiz/lib/reversionTriggerQuiz";

const OPTIONS: ReversionAction[] = ["hold", "drop_magnitude", "full_revert"];

export function QuizReversionTrigger() {
  const {
    question,
    revealedAnswer,
    isCorrect,
    correctAnswer,
    stats,
    submit,
    next,
    bankPosition,
    bankSize,
    isComplete,
    restart,
  } = useQuizSession<ReversionVignette, ReversionAction>({
    quizType: "reversion_trigger",
    bank: REVERSION_TRIGGER_BANK,
    getCorrectAnswer: (q) => q.answer,
    getQuestionPayload: (q) => ({ vignette: q.vignette }),
  });

  return (
    <QuizLayout
      Icon={Brain}
      title="Reversion Triggers"
      subtitle="When the read shifts: hold, lower magnitude, or revert?"
      description={`Tests sheet 8's "Switching Back" section. A read that worked an hour ago can be wrong now: contradicting actions, state changes, time elapsed, seat changes, and your own image shift all demand updating. Failing to revert turns yesterday's edge into today's leak.`}
      stats={stats}
      bankPosition={bankPosition}
      bankSize={bankSize}
      isComplete={isComplete}
      onRestart={restart}
    >
      <div className="rounded-xl border border-border bg-white/[0.02] p-4 text-sm text-text leading-relaxed italic">
        "{question.vignette}"
      </div>

      <p className="text-center text-text text-base">
        Your action on the read?
      </p>

      <div className="grid grid-cols-1 gap-3">
        {OPTIONS.map((act) => (
          <QuizOption
            key={act}
            value={act}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
          >
            <span className="block text-left">{ACTION_LABEL[act]}</span>
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>{ACTION_LABEL[correctAnswer]}</strong>
            </>
          }
          rationale={question.rationale}
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}
